import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2, Layers, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { statusColor, daysLabel } from "@/lib/format";
import ListToolbar from "@/components/ListToolbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = { active: "Active", expiring: "Expiring Soon", expired: "Expired", no_subscription: "No Subscription" };
const PROFILE_FIELDS = [
  ["company_name", "Company Name"], ["trade_name", "Trade Name"], ["gst", "GST"], ["pan", "PAN"],
  ["address", "Address"], ["city", "City"], ["locality", "Locality"], ["contact_person", "Contact Person"],
  ["mobile", "Mobile"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["stock_location", "Stock Location"],
  ["years_in_business", "Years in Business"],
];

export default function Sellers() {
  const [rows, setRows] = useState([]);
  const [cities, setCities] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ plan_type: "", status: "", city: "" });
  const [sort, setSort] = useState("newest");
  const [detail, setDetail] = useState(null);
  const [editSeller, setEditSeller] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [subForm, setSubForm] = useState(null);

  useEffect(() => {
    api.get("/cities?active=true").then(({ data }) => setCities(data));
    api.get("/admin/packages").then(({ data }) => setPackages(data));
  }, []);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (tab !== "all") p.set("role", tab);
    if (search) p.set("q", search);
    if (filters.plan_type) p.set("plan_type", filters.plan_type);
    if (filters.status) p.set("status", filters.status);
    if (filters.city) p.set("city", filters.city);
    p.set("sort", sort);
    const { data } = await api.get(`/admin/sellers?${p.toString()}`);
    setRows(data);
  }, [tab, search, filters, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const openDetail = async (id) => { const { data } = await api.get(`/admin/seller/${id}`); setDetail(data); };

  const openEdit = async (id) => {
    const { data } = await api.get(`/admin/seller/${id}`);
    setEditSeller(data);
    setProfileForm(data.profile || {});
    const s = data.subscription || {};
    setSubForm({
      package_id: s.package_id || (packages[0]?.id || ""), additional_slots: s.additional_slots || 0,
      status: s.status || "active", extend_days: 0,
    });
  };

  const saveProfile = async () => {
    try {
      await api.put(`/admin/sellers/${editSeller.id}`, { ...profileForm, years_in_business: Number(profileForm.years_in_business) || 0 });
      toast.success("Seller profile updated");
      setEditSeller(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSub = async () => {
    try {
      await api.put(`/admin/sellers/${editSeller.id}/subscription`, {
        package_id: subForm.package_id, additional_slots: Number(subForm.additional_slots) || 0,
        status: subForm.status, extend_days: Number(subForm.extend_days) || 0,
      });
      toast.success("Subscription updated");
      setEditSeller(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this seller and ALL their products, payments and promotions? This cannot be undone.")) return;
    try { await api.delete(`/admin/sellers/${id}`); toast.success("Seller deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const resetPassword = async (row) => {
    if (!window.confirm(`Reset password for ${row.company_name}? Their password becomes their mobile number (${row.mobile}) and they must change it at next login.`)) return;
    try {
      const { data } = await api.post(`/admin/sellers/${row.id}/reset-password`);
      toast.success(`Password reset. Temporary password: ${data.temporary_password}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">All Sellers</h1>
        <p className="text-muted-foreground text-sm font-medium">View, edit, manage subscriptions and delete sellers.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="sellers-tab-all">All</TabsTrigger>
          <TabsTrigger value="manufacturer" data-testid="sellers-tab-manufacturer">Manufacturers</TabsTrigger>
          <TabsTrigger value="wholesaler" data-testid="sellers-tab-wholesaler">Wholesalers</TabsTrigger>
          <TabsTrigger value="retailer" data-testid="sellers-tab-retailer">Retailers</TabsTrigger>
        </TabsList>
      </Tabs>

      <ListToolbar
        placeholder="Search company, GST, PAN, mobile, contact…"
        search={search} setSearch={setSearch}
        filters={[
          { key: "plan_type", label: "Package", value: filters.plan_type, options: packages.map((p) => ({ value: p.id, label: `${p.products} Products` })) },
          { key: "status", label: "Status", value: filters.status, options: Object.entries(STATUS).map(([v, l]) => ({ value: v, label: l })) },
          { key: "city", label: "City", value: filters.city, options: cities.map((c) => ({ value: c.name, label: c.name })) },
        ]}
        onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        sort={sort} setSort={setSort}
        sorts={[{ value: "newest", label: "Newest" }, { value: "oldest", label: "Oldest" }, { value: "company", label: "Company Name" }, { value: "expiry", label: "Expiry" }]}
        onReset={() => { setSearch(""); setFilters({ plan_type: "", status: "", city: "" }); setSort("newest"); }}
      />

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Company</TableHead><TableHead>Type</TableHead><TableHead>Mobile</TableHead>
            <TableHead>City</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead>Expiry</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`seller-row-${r.id}`}>
                <TableCell className="font-semibold">{r.company_name}<div className="text-xs text-muted-foreground">{r.trade_name}</div></TableCell>
                <TableCell className="capitalize">{r.role}</TableCell>
                <TableCell className="font-mono text-xs">{r.mobile}</TableCell>
                <TableCell>{r.city || "—"}</TableCell>
                <TableCell className="capitalize">{r.package_label || "—"}</TableCell>
                <TableCell><Badge className={`border ${statusColor(r.status)}`}>{STATUS[r.status] || r.status}</Badge></TableCell>
                <TableCell className="text-xs">{daysLabel(r.days_remaining)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" data-testid={`view-seller-${r.id}`} onClick={() => openDetail(r.id)}>View</Button>
                    <Button size="sm" variant="outline" data-testid={`edit-seller-${r.id}`} onClick={() => openEdit(r.id)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" data-testid={`reset-seller-${r.id}`} title="Reset password" onClick={() => resetPassword(r)}><KeyRound className="h-4 w-4 text-amber-600" /></Button>
                    <Button size="sm" variant="outline" data-testid={`delete-seller-${r.id}`} onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground font-semibold">No sellers found.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* View detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto ct-scroll">
          <DialogHeader><DialogTitle className="font-heading">{detail?.profile?.company_name || "Seller"}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(detail.profile || {}).map(([k, v]) => (
                  <div key={k}><div className="text-xs text-muted-foreground font-semibold capitalize">{k.replace(/_/g, " ")}</div><div className="font-medium">{String(v || "—")}</div></div>
                ))}
              </div>
              <div className="border-t pt-2"><span className="font-bold">{detail.products?.length || 0}</span> products · <span className="font-bold">{detail.payments?.length || 0}</span> payments</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit seller (profile + subscription) */}
      <Dialog open={!!editSeller} onOpenChange={(v) => !v && setEditSeller(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto ct-scroll">
          <DialogHeader><DialogTitle className="font-heading">Edit Seller</DialogTitle></DialogHeader>
          {editSeller && subForm && (
            <div className="space-y-6">
              <div>
                <h3 className="font-bold mb-2">Company Profile</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {PROFILE_FIELDS.map(([k, label]) => (
                    <div key={k}>
                      <Label>{label}</Label>
                      {k === "city" ? (
                        <Select value={profileForm.city || ""} onValueChange={(v) => setProfileForm({ ...profileForm, city: v })}>
                          <SelectTrigger data-testid="edit-seller-city"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input data-testid={`edit-seller-${k}`} value={profileForm[k] ?? ""} onChange={(e) => setProfileForm({ ...profileForm, [k]: e.target.value })}
                          type={k === "years_in_business" ? "number" : "text"} />
                      )}
                    </div>
                  ))}
                </div>
                <Button data-testid="save-seller-profile" onClick={saveProfile} className="font-bold mt-3">Save Profile</Button>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-bold mb-2 flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> Manage Subscription</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Package</Label>
                    <Select value={subForm.package_id} onValueChange={(v) => setSubForm({ ...subForm, package_id: v })}>
                      <SelectTrigger data-testid="edit-sub-plan"><SelectValue placeholder="Select package" /></SelectTrigger>
                      <SelectContent>{packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.products} Products · ₹{p.price}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={subForm.status} onValueChange={(v) => setSubForm({ ...subForm, status: v })}>
                      <SelectTrigger data-testid="edit-sub-status"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Additional Slots</Label><Input data-testid="edit-sub-slots" type="number" min="0" value={subForm.additional_slots} onChange={(e) => setSubForm({ ...subForm, additional_slots: e.target.value })} /></div>
                  <div><Label>Extend by (days)</Label><Input data-testid="edit-sub-extend" type="number" min="0" value={subForm.extend_days} onChange={(e) => setSubForm({ ...subForm, extend_days: e.target.value })} placeholder="e.g. 365" /></div>
                </div>
                <Button data-testid="save-seller-subscription" onClick={saveSub} className="font-bold mt-3">Save Subscription</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
